import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import InfraPalette from './InfraPalette';
import InfraNodeEditor from './InfraNodeEditor';
import InfraDeployPanel from './InfraDeployPanel';
import './InfrastructureBuilder.css';

/**
 * Infrastructure Builder - Drag-and-drop C2 infrastructure designer
 * Allows visual design of: Agent → Redirector(s) → Listener → C2 Server
 */
const InfrastructureBuilder = ({ projectId }) => {
  const [mode, setMode] = useState('design'); // 'design' or 'deploy'
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [linkingFrom, setLinkingFrom] = useState(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);

  // Node type colors
  const nodeColors = {
    agent: '#4CAF50',
    redirector: '#FF9800',
    listener: '#2196F3',
    c2: '#9E9E9E'
  };

  useEffect(() => {
    if (!svgRef.current) return;

    // Initialize D3 force simulation
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(60));

    simulationRef.current = simulation;

    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || !simulationRef.current) return;

    renderGraph();
  }, [nodes, links, selectedNode, linkingFrom]);

  const renderGraph = () => {
    const svg = d3.select(svgRef.current);
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    svg.selectAll('*').remove();

    // Create container group
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Protocol color mapping
    const protocolColors = {
      http: '#3b82f6',    // blue
      https: '#10b981',   // green
      dns: '#8b5cf6',     // purple
      smb: '#f59e0b'      // orange
    };

    // Render links
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => protocolColors[d.protocol] || '#999')
      .attr('stroke-opacity', d => d.linkType === 'backup' ? 0.4 : 0.6)
      .attr('stroke-width', d => d.linkType === 'primary' ? 3 : 2)
      .attr('stroke-dasharray', d => d.linkType === 'backup' ? '5,5' : '0')
      .attr('marker-end', d => `url(#arrowhead-${d.protocol || 'default'})`)
      .attr('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke-opacity', 1)
          .attr('stroke-width', d.linkType === 'primary' ? 4 : 3);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .attr('stroke-opacity', d.linkType === 'backup' ? 0.4 : 0.6)
          .attr('stroke-width', d.linkType === 'primary' ? 3 : 2);
      })
      .append('title')
      .text(d => {
        const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
        const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));

        let portInfo = '';
        if (sourceNode?.config?.port) {
          portInfo += `Source Port: ${sourceNode.config.port}\n`;
        }
        if (targetNode?.config?.port) {
          portInfo += `Target Port: ${targetNode.config.port}\n`;
        }

        return `${sourceNode?.label} → ${targetNode?.label}\n` +
               `Type: ${d.linkType}\n` +
               `Protocol: ${d.protocol?.toUpperCase()}\n` +
               `Priority: ${d.priority}\n` +
               portInfo;
      });

    // Define arrowhead markers for different protocols
    const defs = svg.append('defs');
    Object.entries(protocolColors).forEach(([protocol, color]) => {
      defs.append('marker')
        .attr('id', `arrowhead-${protocol}`)
        .attr('viewBox', '-0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('orient', 'auto')
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .append('svg:path')
        .attr('d', 'M 0,-5 L 10,0 L 0,5')
        .attr('fill', color);
    });

    // Default arrowhead
    defs.append('marker')
      .attr('id', 'arrowhead-default')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#999');

    // Link labels (protocol + priority + port)
    const linkLabels = g.append('g')
      .selectAll('text')
      .data(links)
      .join('text')
      .attr('font-size', '10px')
      .attr('fill', d => protocolColors[d.protocol] || '#999')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .text(d => {
        const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
        const protocol = d.protocol?.toUpperCase() || '';
        const port = targetNode?.config?.port ? `:${targetNode.config.port}` : '';

        if (d.linkType === 'backup') {
          return `${protocol}${port} (B${d.priority})`;
        }
        return `${protocol}${port}`;
      });

    // Render nodes
    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', 'node')
      .call(drag(simulationRef.current));

    // Node circles
    node.append('circle')
      .attr('r', 30)
      .attr('fill', d => nodeColors[d.type] || '#999')
      .attr('stroke', d => selectedNode?.id === d.id ? '#FFF' : '#333')
      .attr('stroke-width', d => selectedNode?.id === d.id ? 4 : 2)
      .attr('cursor', 'pointer')
      .on('click', handleNodeClick)
      .on('contextmenu', handleNodeRightClick);

    // Node labels
    node.append('text')
      .text(d => d.label || d.type)
      .attr('x', 0)
      .attr('y', 45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', '#333')
      .attr('pointer-events', 'none');

    // Node icons (simple text)
    node.append('text')
      .text(d => getNodeIcon(d.type))
      .attr('x', 0)
      .attr('y', 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '24px')
      .attr('fill', '#FFF')
      .attr('pointer-events', 'none');

    // Update positions on tick
    simulationRef.current.nodes(nodes);
    simulationRef.current.force('link').links(links);
    simulationRef.current.alpha(0.3).restart();

    simulationRef.current.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      linkLabels
        .attr('x', d => (d.source.x + d.target.x) / 2)
        .attr('y', d => (d.source.y + d.target.y) / 2);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });
  };

  const getNodeIcon = (type) => {
    const icons = {
      agent: '🖥️',
      redirector: '🔄',
      listener: '📡',
      c2: '⚙️'
    };
    return icons[type] || '•';
  };

  const drag = (simulation) => {
    const dragstarted = (event, d) => {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    };

    const dragged = (event, d) => {
      d.fx = event.x;
      d.fy = event.y;
    };

    const dragended = (event, d) => {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    };

    return d3.drag()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);
  };

  const handleNodeClick = (event, node) => {
    event.stopPropagation();

    if (linkingFrom) {
      // Create link from linkingFrom to this node
      if (linkingFrom.id !== node.id) {
        createLink(linkingFrom, node);
      }
      setLinkingFrom(null);
    } else {
      // Select node for editing
      setSelectedNode(node);
    }
  };

  const handleNodeRightClick = (event, node) => {
    event.preventDefault();
    event.stopPropagation();

    // Start linking from this node
    setLinkingFrom(node);
  };

  const createLink = (source, target) => {
    // Validate link direction (should flow: agent → redirector → listener → c2)
    const order = { agent: 0, redirector: 1, listener: 2, c2: 3 };

    if (order[source.type] >= order[target.type]) {
      alert('Invalid link direction. Flow must be: Agent → Redirector → Listener → C2');
      return;
    }

    // Check if link already exists
    const exists = links.some(l =>
      (l.source.id === source.id && l.target.id === target.id) ||
      (l.source === source.id && l.target === target.id)
    );

    if (exists) {
      alert('Link already exists');
      return;
    }

    // Calculate priority for agent → redirector links (for failover)
    let priority = 0;
    let linkType = 'primary';

    if (source.type === 'agent' && target.type === 'redirector') {
      // Count existing agent → redirector links for this agent
      const existingLinks = links.filter(l => {
        const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
        const targetNode = nodes.find(n => n.id === (typeof l.target === 'object' ? l.target.id : l.target));
        return sourceId === source.id && targetNode?.type === 'redirector';
      });

      priority = existingLinks.length;
      linkType = priority === 0 ? 'primary' : 'backup';
    }

    // Determine protocol based on listener config
    let protocol = 'https';
    if (target.type === 'listener' && target.config.protocol) {
      protocol = target.config.protocol;
    }

    const newLink = {
      source: source.id,
      target: target.id,
      priority,
      linkType,
      protocol,
      id: `${source.id}-${target.id}`
    };

    setLinks([...links, newLink]);
  };

  const handleDrop = (event) => {
    event.preventDefault();

    const nodeType = event.dataTransfer.getData('nodeType');
    if (!nodeType) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const x = event.clientX - svgRect.left;
    const y = event.clientY - svgRect.top;

    const newNode = {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      label: `${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} ${nodes.filter(n => n.type === nodeType).length + 1}`,
      x,
      y,
      config: {}
    };

    setNodes([...nodes, newNode]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const deleteNode = (nodeId) => {
    setNodes(nodes.filter(n => n.id !== nodeId));
    setLinks(links.filter(l => l.source.id !== nodeId && l.target.id !== nodeId));
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  };

  const updateNode = (nodeId, config) => {
    setNodes(nodes.map(n => n.id === nodeId ? { ...n, config, label: config.name || n.label } : n));
  };

  const clearCanvas = () => {
    if (window.confirm('Clear entire canvas? This cannot be undone.')) {
      setNodes([]);
      setLinks([]);
      setSelectedNode(null);
      setLinkingFrom(null);
    }
  };

  return (
    <div className="infrastructure-builder">
      <div className="builder-header">
        <h2>Infrastructure Builder</h2>
        <div className="builder-controls">
          <div className="mode-toggle">
            <button
              className={mode === 'design' ? 'active' : ''}
              onClick={() => setMode('design')}
            >
              Design
            </button>
            <button
              className={mode === 'deploy' ? 'active' : ''}
              onClick={() => setMode('deploy')}
            >
              Deploy
            </button>
          </div>
          <button onClick={clearCanvas} className="btn-clear">
            Clear Canvas
          </button>
        </div>
      </div>

      <div className="builder-content">
        {mode === 'design' ? (
          <>
            <InfraPalette />
            <div
              className="builder-canvas"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <svg ref={svgRef} width="100%" height="100%">
                {/* D3 renders here */}
              </svg>
              {linkingFrom && (
                <div className="linking-indicator">
                  Linking from: <strong>{linkingFrom.label}</strong>
                  <br />
                  <small>Click a node to create link, or right-click to cancel</small>
                </div>
              )}
            </div>
            {selectedNode && (
              <InfraNodeEditor
                node={selectedNode}
                links={links}
                nodes={nodes}
                onUpdate={updateNode}
                onDelete={deleteNode}
                onClose={() => setSelectedNode(null)}
              />
            )}
          </>
        ) : (
          <InfraDeployPanel
            nodes={nodes}
            links={links}
            projectId={projectId}
            onBack={() => setMode('design')}
          />
        )}
      </div>

      <div className="builder-footer">
        <div className="legend">
          <strong>Legend:</strong>
          {Object.entries(nodeColors).map(([type, color]) => (
            <span key={type} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: color }}></span>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </span>
          ))}
        </div>
        <div className="instructions">
          <strong>Instructions:</strong>
          Drag components from palette to canvas • Click to select • Right-click to start linking • Drag to move
        </div>
      </div>
    </div>
  );
};

export default InfrastructureBuilder;
